import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Clock, Bell, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
interface ReportConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  problemType: string;
  isBlocking: boolean;
  estimatedTime?: string;
}
export function ReportConfirmationModal({
  isOpen,
  onClose,
  problemType,
  isBlocking,
  estimatedTime
}: ReportConfirmationModalProps) {
  return <AnimatePresence data-id="element-300">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50" data-id="element-301" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-302">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-id="element-303">
              {/* Success Icon with Animation */}
              <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 flex justify-center" data-id="element-304">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              damping: 15,
              stiffness: 200
            }} className="relative" data-id="element-305">
                  <motion.div animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute inset-0 bg-teal-deep rounded-full blur-2xl" data-id="element-306" />
                  <div className="relative w-20 h-20 bg-teal-deep rounded-full flex items-center justify-center shadow-xl" data-id="element-307">
                    <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-308" />
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-309">
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-center mb-6" data-id="element-310">
                  <h2 className="text-2xl font-bold text-navy mb-2" data-id="element-311">
                    Segnalazione Inviata!
                  </h2>
                  <p className="text-gray-600" data-id="element-312">
                    La tua segnalazione è stata ricevuta con successo
                  </p>
                </motion.div>

                {/* Info Cards */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="space-y-3 mb-6" data-id="element-313">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-314">
                    <div className="flex items-start gap-3" data-id="element-315">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0" data-id="element-316">
                        <Bell className="w-5 h-5 text-teal-700" data-id="element-317" />
                      </div>
                      <div className="flex-1" data-id="element-318">
                        <p className="text-sm font-semibold text-navy mb-1" data-id="element-319">
                          Prossimi Step
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed" data-id="element-320">
                          {isBlocking ? 'Il team di manutenzione è stato allertato immediatamente. Riceverai una chiamata a breve.' : "Il team di manutenzione esaminerà la segnalazione e ti contatterà per coordinare l'intervento."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {estimatedTime && !isBlocking && <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-321">
                      <div className="flex items-start gap-3" data-id="element-322">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-323">
                          <Clock className="w-5 h-5 text-blue-700" data-id="element-324" />
                        </div>
                        <div className="flex-1" data-id="element-325">
                          <p className="text-sm font-semibold text-navy mb-1" data-id="element-326">
                            Tempo Stimato
                          </p>
                          <p className="text-xs text-gray-600" data-id="element-327">
                            Risoluzione prevista: {estimatedTime}
                          </p>
                        </div>
                      </div>
                    </div>}

                  {isBlocking && <div className="bg-red-50 rounded-xl p-4 border border-red-100" data-id="element-328">
                      <div className="flex items-center gap-2" data-id="element-329">
                        <span className="text-red-600 font-semibold text-sm" data-id="element-330">
                          ⚠️ Problema Bloccante
                        </span>
                      </div>
                      <p className="text-xs text-red-700 mt-1" data-id="element-331">
                        Intervento prioritario in corso
                      </p>
                    </div>}
                </motion.div>

                {/* CTA Button */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5
            }} data-id="element-332">
                  <Button onClick={onClose} className="w-full bg-teal-deep hover:bg-teal-700" data-id="element-333">
                    Continua
                    <ArrowRight className="w-5 h-5 ml-2" data-id="element-334" />
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}